import { useQuery } from '@tanstack/react-query';
import {
  GAP_SCAN_BARS,
  type Candle,
  type Gap,
  type GapMode,
  type Timeframe,
} from '@pipyar/core';
import { api } from '@/lib/api';

export interface CandlesResponse {
  symbol: string;
  timeframe: Timeframe;
  storedGmtOffsetSec: number;
  digits: number;
  count: number;
  candles: Candle[];
  live: Candle | null;
}

export interface GapsResponse {
  symbol: string;
  timeframe: Timeframe;
  digits: number;
  storedGmtOffsetSec: number;
  gaps: Gap[];
  totalDetected: number;
}

export interface GapSummaryRow {
  timeframe: Timeframe;
  bars: number;
  up: number;
  down: number;
  total: number;
  filled: number;
  nearestUp: number | null;
  nearestDown: number | null;
  lastBarTime: number | null;
}

export interface GapSummaryResponse {
  symbol: string;
  digits: number;
  storedGmtOffsetSec: number;
  timeframes: GapSummaryRow[];
}

export interface InstrumentDto {
  id: number;
  symbol: string;
  description: string;
  digits: number;
  point: number;
  tickSize: number;
  contractSize: number;
  profitCurrency: string;
  storedGmtOffsetSec: number;
  enabled: boolean;
}

export function useInstruments() {
  return useQuery({
    queryKey: ['instruments'],
    queryFn: () => api.get<{ instruments: InstrumentDto[] }>('/api/market/instruments'),
    staleTime: 5 * 60_000,
  });
}

export function useCandles(symbol: string | undefined, timeframe: Timeframe, limit = 1000) {
  return useQuery({
    queryKey: ['candles', symbol, timeframe, limit],
    queryFn: () =>
      api.get<CandlesResponse>(
        `/api/market/candles?symbol=${encodeURIComponent(symbol!)}&timeframe=${timeframe}&limit=${limit}&includeLive=1`,
      ),
    enabled: Boolean(symbol),
    /*
     * The cadence follows the size of the request.
     *
     * A closed M1 bar arrives at most once a minute, so 30s is already polling
     * for its own sake — but it is affordable at a thousand bars. The gaps
     * module asks for the whole M1 series, which is ~13 MB of JSON and 2 MB on
     * the wire; refetching that every 30s would spend a quarter of a gigabyte
     * an hour and re-parse it in the browser often enough to be felt.
     *
     * Nothing in that history changes — only the tail does — so the big request
     * is repeated rarely. The gap levels themselves come from their own small
     * endpoint, which keeps its faster refresh.
     */
    staleTime: limit > 20_000 ? 4 * 60_000 : 20_000,
    refetchInterval: limit > 20_000 ? 5 * 60_000 : 30_000,
  });
}

export function useGaps(
  symbol: string | undefined,
  timeframe: Timeframe,
  opts: { limit?: number; minPips?: number; includeFilled?: boolean; mode?: GapMode } = {},
) {
  const limit = opts.limit ?? GAP_SCAN_BARS[timeframe];
  const minPips = opts.minPips ?? 0;
  const includeFilled = opts.includeFilled ?? false;
  const mode = opts.mode ?? 'untraded';

  return useQuery({
    queryKey: ['gaps', symbol, timeframe, limit, minPips, includeFilled, mode],
    queryFn: () =>
      api.get<GapsResponse>(
        `/api/market/gaps?symbol=${encodeURIComponent(symbol!)}&timeframe=${timeframe}` +
          `&limit=${limit}&minPips=${minPips}&mode=${mode}` +
          `${includeFilled ? '&includeFilled=1' : ''}`,
      ),
    enabled: Boolean(symbol),
    staleTime: 20_000,
    refetchInterval: 30_000,
  });
}

export function useGapSummary(
  symbol: string | undefined,
  minPips = 0,
  mode: GapMode = 'untraded',
) {
  return useQuery({
    queryKey: ['gap-summary', symbol, minPips, mode],
    queryFn: () =>
      api.get<GapSummaryResponse>(
        // No limit parameter: the server uses GAP_SCAN_BARS per timeframe so the
        // counts here always match the chart page.
        `/api/market/gaps/summary?symbol=${encodeURIComponent(symbol!)}&minPips=${minPips}&mode=${mode}`,
      ),
    enabled: Boolean(symbol),
    // The dashboard polls fast: these summaries are cheap (tens of
    // milliseconds server-side) and a board you glance at should not be showing
    // a price from a minute ago. React Query pauses the timer while the tab is
    // in the background, so this costs nothing when you are not looking.
    staleTime: 4_000,
    refetchInterval: 5_000,
  });
}
